import Head from 'next/head';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { getBreadcrumbSchema } from 'productTemplate/utils/getBreadcrumbSchema';
import { getCollectionPageSchema } from 'productTemplate/utils/getCollectionPageSchema';
import { IBreadcrumb } from 'shared/types/Breadcrumbs';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { startCase } from 'shared/utils/startCase';
import { useCurrency } from 'shared/hooks/useCurrency';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { t } from 'localization/utils/t';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { setIsHeaderVisible } from 'store/slice/pageLayoutSlice';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import ProductSearchGrid from 'shared/components/product-grid';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { usePageMetadataCS } from 'shared/hooks/usePageMetadataCS';
import {
  getFashionSchema,
  getSneakerSchema,
} from 'product-landing-pages/utils/createPlpHeadMetadata';

const TP = 'productLandingPages.components.PlpPageComponent';

export interface IPlpPageComponent {
  brandName?: string;
  breadcrumbs?: IBreadcrumb[];
  browseKey: string;
  browseValue: string;
  constructorResponse: IConstructorBrowseResponse;
  category?: string;
  description?: string;
  displayName: string;
  globalPricingId: string;
  hasBreadcrumbSchema?: boolean;
  hasLink?: boolean;
  linkDestination?: string;
  linkTitle?: string;
  metadataDescription?: string;
  metaImage?: string;
  metaTitle?: string;
  silhouette?: string;
  isCrawler: boolean;
  consumerSearchFilters?: any;
  isBrandsPath?: boolean;
  consumerSearchInitialData?: any;
}

export const PlpPageComponent = ({
  breadcrumbs,
  category,
  constructorResponse,
  description = null,
  displayName,
  globalPricingId,
  hasBreadcrumbSchema = false,
  hasLink,
  linkTitle,
  metadataDescription,
  metaImage,
  metaTitle,
  silhouette,
  isCrawler,
  consumerSearchFilters = {},
  consumerSearchInitialData = {},
  isBrandsPath = false,
}: IPlpPageComponent) => {
  const { locale, query, asPath } = useRouter();
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const metadataCS = usePageMetadataCS({
    consumerSearchData: consumerSearchInitialData,
    mainTitle: metaTitle,
    mainDescription: metadataDescription,
    defaultTitle: t(`${TP}.metaTitle`, 'Buy {displayName} ', {
      displayName,
    }),
  });

  const metadataLegacy = usePageMetadata({
    constructorResponse,
    mainTitle: metaTitle,
    mainDescription: metadataDescription,
    defaultTitle: t(`${TP}.metaTitle`, 'Buy {displayName} ', {
      displayName,
    }),
  });

  const { pageTitle, pageDescription } = isNewSearchPageEnabled ? metadataCS : metadataLegacy;
  const { selectedCurrencyCode } = useCurrency();

  // aDDING consumer search
  const storeDispatch = useStoreDispatch();
  const toggleHeader = (status) => {
    storeDispatch(setIsHeaderVisible(status));
  };

  const allCSFilters = useMemo(() => {
    return omit(['theme', 'collectionSlug'], consumerSearchFilters);
  }, [consumerSearchFilters]);

  return (
    <PlpPageComponent.Container>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" key="og:title" content={pageTitle} />
        <meta name="twitter:title" content={pageTitle} key="twitter:title" />
        <meta
          name="description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="description"
        />
        <meta
          property="og:description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="og:description"
        />
        <meta
          name="twitter:description"
          content={
            pageDescription ||
            t(`${TP}.metaDescription`, 'Shop {displayName} now on GOAT. Authenticity Assured.', {
              displayName,
            })
          }
          key="twitter:description"
        />
        {metaImage && (
          <>
            <meta name="twitter:image" content={metaImage} key="twitter:image" />
            <meta property="og:image" content={metaImage} key="og:image" />
          </>
        )}
        {hasBreadcrumbSchema && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: getBreadcrumbSchema(
                category === 'sneakers'
                  ? getSneakerSchema(breadcrumbs, silhouette)
                  : getFashionSchema(breadcrumbs),
                locale,
              ),
            }}
          />
        )}
        {query?.silhouette && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: getCollectionPageSchema({
                constructorResponse,
                locale,
                asPath,
                description: metadataDescription,
                silhouette: startCase(silhouette),
                query,
                globalPricingId,
                selectedCurrencyCode,
              }),
            }}
          />
        )}
      </Head>
      <div style={{ marginTop: 0, overflow: 'hidden' }}>
        <ProductSearchGrid
          toggleHeader={toggleHeader}
          collectionSlug={consumerSearchFilters?.collectionSlug || ''}
          collectionDetails={undefined}
          isCrawler={isCrawler}
          pageFilters={allCSFilters}
          initialData={consumerSearchInitialData}
          pageAttributes={{
            hasLink,
            linkTitle,
            title: displayName,
            description: isBrandsPath ? description || '' : description,
            globalPricingId,
            breadcrumbs,
            isBrandsPath,
            theme: consumerSearchFilters?.theme,
          }}
        />
      </div>
    </PlpPageComponent.Container>
  );
};

PlpPageComponent.Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  position: relative;
`;
